import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CreditCard,
  FileText,
  Shield,
  Cpu,
  Handshake,
  Plus,
  X,
  ExternalLink,
  Link2,
} from "lucide-react";
import {
  TICKET_ENTITY_META,
  TICKET_ENTITY_TYPES,
} from "constants/tickets";
import LinkPickerField from "components/Tickets/LinkPickerField";

const ENTITY_ICON = {
  company: Building2,
  mid: CreditCard,
  application: FileText,
  disputifierEnrollment: Shield,
  processor: Cpu,
  partner: Handshake,
};

/**
 * EntityLinkPanel (SOUND-497) — workspace right rail.
 * -------------------------------------------------------
 * Lists a ticket's entity links (company / MID / application / Disputifier /
 * processor / partner). Each link is a chip that deep-links to that entity's
 * detail page. Internal users can add (entity-type picker + scoped search) and
 * remove; clients/partners see links read-only. Optimistic add/remove is owned
 * by the page (onAddLink / onRemoveLink resolve/throw); this component only
 * drives the form UI.
 */
export default function EntityLinkPanel({
  links = [],
  companyId,
  isInternal = false,
  onAddLink,
  onRemoveLink,
}) {
  const [adding, setAdding] = useState(false);
  const [entityType, setEntityType] = useState("mid");
  const [busy, setBusy] = useState(false);

  const submit = async ({ entityId, label }) => {
    setBusy(true);
    try {
      await onAddLink?.({ entityType, entityId, label, role: "related" });
      setAdding(false);
    } catch {
      /* page surfaces the toast */
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Link2 className="h-3.5 w-3.5" />
          Linked {links.length > 0 && <span className="text-muted-foreground/70">· {links.length}</span>}
        </h3>
        {isInternal && !adding && (
          <button
            type="button"
            className="btn btn-ghost h-7 px-2 text-xs"
            onClick={() => setAdding(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Link
          </button>
        )}
      </div>

      {links.length === 0 && !adding && (
        <p className="text-sm text-muted-foreground">No linked entities.</p>
      )}

      <ul className="space-y-1.5">
        {links.map((l) => {
          const meta = TICKET_ENTITY_META[l.entityType] || { label: l.entityType, linkTo: null };
          const Icon = ENTITY_ICON[l.entityType] || Link2;
          const to = meta.linkTo ? meta.linkTo(l.entityId) : null;
          const label = l.label || l.entityId;
          // SOUND-611: the primary company link IS the ticket's owning client —
          // scope identity, not an operator-managed association. The API rejects
          // deleting it (400), so don't offer a control that can only fail; mark
          // it as the client instead.
          const isPrimaryCompany = l.entityType === "company" && l.role === "primary";
          return (
            <li
              key={l.linkId}
              className={`group flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 ${
                l._optimistic ? "opacity-60" : ""
              }`}
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{label}</div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {meta.label}
                  {isPrimaryCompany && " · Client"}
                </div>
              </div>
              {to && (
                <Link
                  to={to}
                  className="text-muted-foreground hover:text-brand"
                  title={`Open ${meta.label}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
              {isInternal && !l._optimistic && !isPrimaryCompany && (
                <button
                  type="button"
                  onClick={() => onRemoveLink?.(l.linkId)}
                  className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                  title="Remove link"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {isInternal && adding && (
        <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/20 p-2.5">
          <div className="flex items-center justify-between">
            <select
              className="select h-8 w-auto text-sm"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            >
              {TICKET_ENTITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TICKET_ENTITY_META[t].label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-ghost h-7 px-2 text-xs"
              onClick={() => setAdding(false)}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
          <LinkPickerField
            entityType={entityType}
            companyId={companyId}
            onSelect={submit}
            placeholder={`Search ${TICKET_ENTITY_META[entityType].label.toLowerCase()}…`}
            autoFocus
          />
        </div>
      )}
    </section>
  );
}
